import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { generateHashKey } from "./hashUtils";
import moment from "moment";
// @ts-ignore — moment locale files ship as JS without TS declarations
import "moment/locale/fr";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { enGB } from "date-fns/locale/en-GB";

registerLocale("fr", fr);
registerLocale("en-GB", enGB);

export const SUPPORTED_LANGUAGES = {
    "en-GB": { name: "en-GB", nativeName: "English", flag: "🇬🇧" },
    fr: { name: "fr-FR", nativeName: "Français", flag: "🇫🇷" },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;
export const DEFAULT_LANGUAGE: SupportedLanguage = "en-GB";
export const FALLBACK_LANGUAGE: SupportedLanguage = "en-GB";

const hashKeyProcessor = {
    type: "postProcessor" as const,
    name: "hashKeyProcessor",
    process(
        value: string,
        key: string | string[],
        options: Record<string, unknown>,
        translator: {
            exists: (k: string) => boolean;
            translate: (k: string, opts?: Record<string, unknown>) => string;
        }
    ) {
        const textKey = Array.isArray(key) ? key[0] : key;
        const hashKey = generateHashKey(textKey);
        if (translator.exists(hashKey)) {
            const translated = translator.translate(hashKey, options);
            if (translated) return translated;
        }
        return value;
    },
};

i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .use(hashKeyProcessor)
    .init({
        debug: process.env.NODE_ENV === "development",
        lng: DEFAULT_LANGUAGE,
        fallbackLng: FALLBACK_LANGUAGE,
        supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
        ns: ["translation"],
        defaultNS: "translation",
        postProcess: ["hashKeyProcessor"],
        nsSeparator: false,
        keySeparator: false,
        interpolation: { escapeValue: false },
        backend: {
            loadPath: (lngs: string[], _ns: string[]) => {
                const lng = lngs[0];
                if (lng === "fr") return "/locales/fr-FR/fr.json";
                return "/locales/en-GB/en.json";
            },
        },
        detection: {
            order: ["localStorage", "navigator", "htmlTag"],
            caches: ["localStorage"],
            lookupLocalStorage: "i18nextLng",
        },
        react: { useSuspense: true },
    });

i18n.on("languageChanged", (lng: string) => {
    document.documentElement.setAttribute("lang", lng);
    moment.locale(lng);
    setDefaultLocale(lng);
});

export default i18n;
